import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import {
  Box,
  Button,
  Group,
  GroupSeparator,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Separator,
  Text,
  useIsMobile,
} from "@salestrak/ui";

import { PageHeader } from "#app/components/page-header";
import { MembersTable } from "#app/components/members/members-table";
import { useEntityDetail } from "#app/components/entity";
import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { MEMBER_LIST } from "#app/lib/filters/list-configs";
import type { RoleRow, TeamOption } from "#app/lib/data";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useCanInvite } from "#app/lib/hooks/use-can-invite";
import { usePermission } from "#app/lib/hooks/use-permission";

function MembersScreen() {
  const { push } = useAppNavigate();
  const isMobile = useIsMobile();
  const canManageUsers = usePermission("manage.users");
  const canInvite = useCanInvite();
  const [filterWhere, setFilterWhere] = useState<Record<string, unknown>>();

  const {
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    searchWhere,
    orderBy,
    where,
  } = useListQueryState(MEMBER_LIST, { filterWhere });

  // Sibling data extracted from the ungrouped query.
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const handleSiblingData = useCallback((r: RoleRow[], t: TeamOption[]) => {
    setRoles(r);
    setTeams(t);
  }, []);

  // Dialog state
  const { openEntity } = useEntityDetail();
  const [inviteOpen, setInviteOpen] = useState(false);

  const handleViewProfile = useCallback(
    (userId: string) => openEntity("user", userId),
    [openEntity],
  );

  const handleGoToMemberSettings = useCallback(() => {
    push("/settings/members");
  }, [push]);

  return (
    <Box direction="column" fill="flex">
      <PageHeader
        title="Members"
        actions={
          // Invite visibility is grant-driven (role_invite_grant); the
          // member-settings shortcut stays behind manage.users.
          canInvite && canManageUsers ? (
            <Menu>
              <Group>
                <Button
                  size={isMobile ? "icon-xs" : "xs"}
                  onClick={() => setInviteOpen(true)}
                >
                  {isMobile ? <Icon name="plus" /> : <Text>Invite members</Text>}
                </Button>
                <GroupSeparator />
                <MenuTrigger
                  render={<Button aria-label="More invite actions" size="icon-xs" />}
                >
                  <Icon name="chevron-down" />
                </MenuTrigger>
              </Group>
              <MenuPopup align="end">
                <MenuItem onClick={handleGoToMemberSettings}>
                  <Icon name="settings" color="muted" />
                  <Text size="sm">Go to member settings</Text>
                </MenuItem>
              </MenuPopup>
            </Menu>
          ) : canInvite ? (
            <Button
              size={isMobile ? "icon-xs" : "xs"}
              onClick={() => setInviteOpen(true)}
            >
              {isMobile ? <Icon name="plus" /> : <Text>Invite members</Text>}
            </Button>
          ) : canManageUsers ? (
            <Button
              size={isMobile ? "icon-xs" : "xs"}
              onClick={handleGoToMemberSettings}
            >
              {isMobile ? <Icon name="settings" /> : <Text>Member settings</Text>}
            </Button>
          ) : undefined
        }
      />
      <Separator />
      <MembersTable
        variant="app"
        where={where}
        orderBy={orderBy}
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        availableTeams={teams}
        availableRoles={roles}
        onViewProfile={handleViewProfile}
        onFilterWhereChange={setFilterWhere}
        searchWhere={searchWhere}
        onSiblingData={handleSiblingData}
      />
      {canInvite && (
        <InviteMembersDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
        />
      )}
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/members")({
  component: MembersScreen,
});
